import { useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import { Zap } from "lucide-react"
import {
  fieldInputClass,
  fieldLabelClass,
  finalBulletClass,
  focusRing,
  formButtonClass,
  logEventClass,
  logTimeClass,
  timerClass,
} from "../lightTheme"
import { countryParamPresent, useLandingCountryDetection } from "../landingCountryDetect"
import { landingCopy } from "../landingCopy"
import { landingMarket } from "../landingMarket"
import { useLandingLanguage } from "../useLandingLanguage"
import { buildTestDriveLead, leadCountryForTestDrive } from "./testDriveLead"
import {
  TEST_DRIVE_PLAYBACK_MS,
  testDrivePlaybackFrame,
  withSubmittedPhone,
} from "./testDrivePlayback"

const previewCardShadow =
  "shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_2px_2px_0_rgba(0,0,0,0.04),0_8px_16px_-4px_rgba(0,0,0,0.04),0_0_0_1px_#FAFAFA]"

const driveFieldClass = cn(
  fieldInputClass,
  "bg-[#F2F2F2] text-[#171717] shadow-[0_0_0_1px_#E6E6E6]"
)

const filledLineClass = "text-[16px] font-normal leading-6 text-[#171717]"

const previewTabs = [
  { id: "lead", kind: "form" },
  { id: "qualifying", kind: "log", count: 3, showFinal: true },
]

function DriveField({ id, label, type = "text", inputMode, value, onChange }) {
  return (
    <div className="flex w-full flex-col gap-3">
      <Label className={fieldLabelClass} htmlFor={id}>
        {label}
      </Label>
      <Input
        id={id}
        type={type}
        inputMode={inputMode}
        placeholder=""
        value={value}
        onChange={onChange}
        className={driveFieldClass}
      />
    </div>
  )
}

function LogLine({ time, event }) {
  return (
    <p className="text-[12px] leading-4">
      <span className={logTimeClass}>{time}</span>{" "}
      <span className={logEventClass}>{event}</span>
    </p>
  )
}

function FilledLine({ label, value }) {
  return (
    <p className={filledLineClass}>
      <span className="font-bold">{label}</span> {value}
    </p>
  )
}

function backendUrl() {
  return (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")
}

function releaseSubmit(submittingRef, setSubmitting) {
  submittingRef.current = false
  setSubmitting(false)
}

function CaptureForm({ onSent }) {
  const language = useLandingLanguage()
  const testDrive = landingCopy(language, "testDrive")
  const detection = useLandingCountryDetection()
  const [searchParams] = useSearchParams()
  const [name, setName] = useState("")
  const [phone, setPhone] = useState("")
  const [email, setEmail] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const submittingRef = useRef(false)

  async function submitTestDrive(event) {
    event.preventDefault()
    if (submittingRef.current) return
    submittingRef.current = true
    setSubmitting(true)
    let sentPhone = null
    try {
      const detectedCountry = countryParamPresent(searchParams)
        ? null
        : await detection.whenSettled()
      const country = leadCountryForTestDrive(searchParams, detectedCountry)
      const market = landingMarket(country)
      if (market == null) return
      const lead = buildTestDriveLead({
        name,
        phone,
        email,
        country,
        pageUrl: window.location.href,
        search: searchParams,
      })
      if (lead === null) return
      const url = backendUrl()
      if (!url) return
      const response = await fetch(`${url}/api/marketing/roofing-light-test-drive`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ market, lead }),
      })
      if (response.ok) sentPhone = phone
    } finally {
      releaseSubmit(submittingRef, setSubmitting)
    }
    if (sentPhone != null) onSent(sentPhone)
  }

  return (
    <form className="flex flex-col gap-6 p-6" onSubmit={submitTestDrive}>
      <div className="flex flex-col gap-1">
        <FilledLine label={testDrive.serviceLabel} value={testDrive.serviceValue} />
        <FilledLine label={testDrive.addressLabel} value={testDrive.addressValue} />
      </div>
      <DriveField
        id="lead-name"
        label={testDrive.nameLabel}
        value={name}
        onChange={(event) => setName(event.target.value)}
      />
      <DriveField
        id="direct-phone"
        label={testDrive.phoneLabel}
        inputMode="tel"
        value={phone}
        onChange={(event) => setPhone(event.target.value)}
      />
      <DriveField
        id="lead-email"
        label={testDrive.emailLabel}
        type="email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
      />
      <Button type="submit" disabled={submitting} className={`${formButtonClass} gap-1.5`}>
        <Zap strokeWidth={1.5} className="text-white" aria-hidden="true" />
        {testDrive.cta}
      </Button>
    </form>
  )
}

export default function TestDriveSection() {
  const language = useLandingLanguage()
  const testDrive = landingCopy(language, "testDrive")
  const [activeTab, setActiveTab] = useState("lead")
  const [playback, setPlayback] = useState(null)
  const [elapsedMs, setElapsedMs] = useState(0)
  const tabIndex = Math.max(previewTabs.findIndex((item) => item.id === activeTab), 0)
  const tab = previewTabs[tabIndex]
  const live = playback != null && tab.id === "qualifying"
  const frame = live ? testDrivePlaybackFrame(elapsedMs) : null
  const visibleLogs = testDrive.logs.slice(0, live ? frame.visibleCount : tab.count ?? 0)

  useEffect(() => {
    if (!playback) return undefined
    let frameId = 0
    const tick = () => {
      const elapsed = performance.now() - playback.startedAt
      setElapsedMs(elapsed)
      if (elapsed < TEST_DRIVE_PLAYBACK_MS) frameId = requestAnimationFrame(tick)
    }
    frameId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frameId)
  }, [playback])

  function handleSent(phone) {
    setActiveTab("qualifying")
    setElapsedMs(0)
    setPlayback({ phone, startedAt: performance.now() })
  }

  return (
    <section className="ml-[calc(50vw-50%-1.5rem)] grid w-full grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-8">
      <div className="flex flex-col gap-8 lg:col-span-6 lg:col-start-1">
        <h2 className="text-balance text-[40px] font-bold leading-[48px] tracking-[-0.04em] text-[#171717] max-md:text-[28px] max-md:leading-8">
          {testDrive.title}
        </h2>
        <p className="max-w-[64ch] text-[18px] font-normal leading-7 text-[#4D4D4D]">
          {testDrive.subtitle}
        </p>
      </div>
      <div className="flex min-w-0 flex-col gap-4 lg:col-span-6 lg:col-start-7">
        <div
          role="tablist"
          aria-label={testDrive.ariaLabel}
          className="flex w-fit flex-wrap gap-1 self-start lg:grid lg:w-full lg:grid-cols-2"
        >
          {previewTabs.map((item, index) => {
            const selected = item.id === tab.id
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setActiveTab(item.id)}
                className={cn(
                  "h-8 w-fit cursor-pointer whitespace-nowrap rounded-[6px] px-3.5 text-[14px] font-normal leading-5",
                  focusRing,
                  selected
                    ? "bg-[#EBEBEB] text-[#171717]"
                    : "text-[#4D4D4D] hover:text-[#171717]"
                )}
              >
                {testDrive.tabs[index]}
              </button>
            )
          })}
        </div>
        <div
          role="tabpanel"
          className={`flex min-h-[354px] w-full flex-col overflow-hidden rounded-xl bg-white ${previewCardShadow}`}
        >
          <div className="relative flex h-11 shrink-0 items-center px-4 shadow-[inset_0_-1px_0_rgba(0,0,0,0.08)]">
            <span className="flex items-center gap-1" aria-hidden="true">
              <span className="size-2 rounded-full bg-[#EE6D5E]" />
              <span className="size-2 rounded-full bg-[#F3BF4A]" />
              <span className="size-2 rounded-full bg-[#5DC753]" />
            </span>
            <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 text-[13px] font-normal leading-5 text-[#7D7D7D]">
              {testDrive.tabs[tabIndex]}
            </span>
          </div>
          {tab.kind === "form" ? (
            <CaptureForm onSent={handleSent} />
          ) : (
            <div className="flex flex-col p-6">
              <p className={timerClass}>{live ? frame.clock : testDrive.timer}</p>
              <div className="mt-6">
                {visibleLogs.map((line) => (
                  <LogLine
                    key={line.time}
                    time={line.time}
                    event={live ? withSubmittedPhone(line.event, playback.phone) : line.event}
                  />
                ))}
                {(live ? frame.showFinal : tab.showFinal) ? (
                  <p className="text-[12px] leading-4">
                    <span className={finalBulletClass}>●</span>{" "}
                    <span className={logEventClass}>{testDrive.finalStatus}</span>
                  </p>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
